import { ChangeEvent, useCallback } from 'react';
import styled from 'styled-components';
import { useQuoteBuilderView } from '@paradigm/features/src/unified-rfqs/ui/grfq/quote-builder/view';
import { ripeLemon, nepal } from '@paradigm/design-system/src/colors';
import { useQuoteBuilderActions } from '@paradigm/features/src/unified-rfqs/ui/grfq/quote-builder/form-context';
import {
  LegsTableContainer as BaseContainer,
  Header,
  Row,
  TableHeader,
} from '#/components/rfq-common/LegsTable';
import { ClickableSpan } from '#/components/UnifiedRfqs/components//ClickableSpan';
import { LegPriceView } from '@paradigm/features/src/unified-rfqs/ui/grfq/quote-builder/views/legsPrices';
import { track } from '@paradigm/analytics/src/analytics';
import { formatPrecision } from '@paradigm/utils/formatDecimal';
import { NEGATIVE_SIGN } from '@paradigm/utils/src/enhanceNegativeSign';
import FormatterInput from '#/components/UnifiedRfqs/components/FormatterInput';
import {
  useDebouncedTrackAction,
  useTrackAction,
} from '@paradigm/features/src/analytics';

export function MarkPriceLegs() {
  const { legsPrices } = useQuoteBuilderView();

  return (
    <Container css="padding-top 42px; width: 120px">
      <TableHeader>
        <MarkPriceRow>
          <span>Mark Price</span>
        </MarkPriceRow>
      </TableHeader>
      {legsPrices.legs.map((leg, index) => (
        <MarkPriceRowValue index={index} key={leg.key} legPriceView={leg} />
      ))}
    </Container>
  );
}

interface MarkPriceRowValueProps {
  readonly index: number;
  readonly legPriceView: LegPriceView;
}

function MarkPriceRowValue({ index, legPriceView }: MarkPriceRowValueProps) {
  const { markPrice, precision, markPriceIv } = legPriceView;
  const isMarkEmpty = markPrice === '-';
  const isMarkIvEmpty = markPriceIv === '';
  const shouldRenderMarkIv = !isMarkIvEmpty;
  const trackAction = useTrackAction();
  const { changeLegPrice } = useQuoteBuilderActions();
  const selectMarkPrice = useCallback(() => {
    // old tracking code.
    // will remove after confirming the new tracking code works (https://tradeparadigm.atlassian.net/browse/PTP-20626)
    track(`grfqs__markPrice__clicked`, { markPrice });
    // new tracking code
    trackAction('select mark price');
    changeLegPrice({ index, side: legPriceView.side, value: markPrice });
  }, [changeLegPrice, index, legPriceView.side, markPrice, trackAction]);

  return !shouldRenderMarkIv ? (
    <MarkPriceRow>
      <ClickableSpan
        onClick={selectMarkPrice}
        data-testid={`leg-${index + 1}-mark-price`}
      >
        {isMarkEmpty ? NEGATIVE_SIGN : formatPrecision(markPrice, precision)}
      </ClickableSpan>
    </MarkPriceRow>
  ) : (
    <MarkPriceRowIv>
      <ClickableSpan
        onClick={selectMarkPrice}
        data-testid={`leg-${index + 1}-mark-price`}
      >
        {isMarkEmpty ? NEGATIVE_SIGN : formatPrecision(markPrice, precision)}
      </ClickableSpan>
      <MarkPriceIv>{markPriceIv}%</MarkPriceIv>
    </MarkPriceRowIv>
  );
}

export function InputLegsPrice() {
  const { legsPrices } = useQuoteBuilderView();
  const { changeLegPrice } = useQuoteBuilderActions();
  const trackAction = useDebouncedTrackAction();
  const createChangeHandler = useCallback(
    (index: number, side: 'BUY' | 'SELL') =>
      (event: ChangeEvent<HTMLInputElement>) => {
        trackAction('change leg price', { index, side });
        changeLegPrice({ index, side, value: event.target.value });
      },
    [changeLegPrice, trackAction],
  );
  return (
    <Container
      css={`
        width: 200px;
        box-shadow: inset 0px 0px 0px 1px ${ripeLemon};
      `}
      aria-label="strategy leg prices"
    >
      <Header css="font-size: 0.8rem;">Leg Prices</Header>
      <TableHeader>
        <InputPriceRow>
          <span>Bid</span>
          <span>Ask</span>
        </InputPriceRow>
      </TableHeader>
      {legsPrices.legs.map((leg, index) => (
        <InputPriceRow key={leg.key}>
          <FormatterInput
            aria-label={`leg ${index + 1} bid price`}
            disabled={leg.side === 'SELL'}
            thousandSeparator
            decimalScale={leg.precision}
            value={leg.bid}
            allowNegative={false}
            placeholder={leg.placeholder}
            onChange={createChangeHandler(index, 'BUY')}
          />
          <FormatterInput
            aria-label={`leg ${index + 1} ask price`}
            disabled={leg.side === 'BUY'}
            thousandSeparator
            decimalScale={leg.precision}
            value={leg.ask}
            allowNegative={false}
            placeholder={leg.placeholder}
            onChange={createChangeHandler(index, 'SELL')}
          />
        </InputPriceRow>
      ))}
    </Container>
  );
}

export const Container = styled(BaseContainer)`
  margin-left: 5px;
`;

export const MarkPriceRow = styled(Row)`
  grid-template-columns: auto;
`;

export const MarkPriceIv = styled.span`
  height: 12px;
  width: 34px;
  opacity: 0.3;
  font-size: 10px;
  font-weight: bold;
  letter-spacing: 0;
  line-height: 11px;
  color: #ffffff;
`;

export const MarkPriceRowIv = styled.div`
  line-height: 14px;
  font-size: 13px;
  color: ${nepal};
  font-family: 'IBM Plex Sans';
  display: grid;
  padding: 0px 12px;
  padding-top: 10px;
  align-items: center;
`;

export const InputPriceRow = styled(Row)`
  grid-template-columns: auto auto;
`;
